import React, { useState } from 'react';
import axios from 'axios';
import { toast } from './ui/sonner';
import { CheckCircle2, AlertTriangle, Loader2, Send, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };

const STATUS = {
  not_configured: { label: 'Not configured', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Mail,
    text: 'Enter your SMTP host, credentials and sender address, then save to enable the connector.' },
  configured: { label: 'Configured — not yet verified', cls: 'bg-blue-50 text-blue-800 border-blue-200', Icon: Mail,
    text: 'Settings are saved and the password is encrypted at rest. Send a test e-mail to verify the connection.' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2,
    text: 'The connector delivered a test e-mail successfully. Changing any SMTP setting resets this status.' },
  failed_test: { label: 'Last test failed', cls: 'bg-red-50 text-red-800 border-red-200', Icon: AlertTriangle,
    text: 'The last test e-mail could not be sent. Review the error below, update the settings and test again.' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);

export const SmtpTestPanel = ({ branding, defaultRecipient, onTested }) => {
  const [recipient, setRecipient] = useState(defaultRecipient || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const status = branding?.smtp_status || 'not_configured';
  const s = STATUS[status] || STATUS.not_configured;
  const lastError = result && !result.ok ? result.message : branding?.smtp_last_test_error;
  const lastAt = result?.tested_at || branding?.smtp_last_test_at;

  const sendTest = async () => {
    const to = recipient.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { toast.error('Enter a valid recipient e-mail address'); return; }
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/company/smtp/test`, { recipient: to }, { withCredentials: true });
      setResult(data);
      if (data.ok) toast.success(data.message); else toast.error(data.message);
      onTested?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(' ') : 'Test e-mail failed';
      setResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="smtp-test-panel">
      <div className={`rounded-md border px-4 py-3 text-xs flex items-start gap-3 ${s.cls}`} style={FONT} data-testid="smtp-status-banner" data-status={status}>
        <s.Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold text-sm" data-testid="smtp-status-label">{s.label}</p>
          <p className="mt-0.5">{s.text}</p>
          {lastAt && <p className="mt-1 opacity-80" data-testid="smtp-last-test-at">Last test: {fmt(lastAt)}{branding?.smtp_last_test_to ? ` → ${branding.smtp_last_test_to}` : ''}</p>}
          {lastError && status !== 'verified' && <p className="mt-1 font-mono break-words" data-testid="smtp-last-test-error">{lastError}</p>}
        </div>
      </div>

      {status !== 'not_configured' && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={FONT}>Send a test e-mail</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="you@yourcompany.com"
              data-testid="smtp-test-recipient"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
              style={FONT}
            />
            <button
              type="button"
              onClick={sendTest}
              disabled={sending}
              data-testid="smtp-send-test-button"
              className="px-4 py-2 text-sm font-semibold text-white rounded-md disabled:opacity-50 flex items-center justify-center gap-2 hover:brightness-110 transition-all"
              style={{ background: 'var(--brand-primary, #002FA7)', ...FONT }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Sending…' : 'Send test email'}
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2" style={FONT}>
            Uses the <strong>saved</strong> settings above — save first if you just changed them. Limited to 5 tests per 15 minutes.
          </p>
          {result && (
            <p className={`mt-2 text-xs font-medium ${result.ok ? 'text-emerald-700' : 'text-red-700'}`} style={FONT} data-testid="smtp-test-result" data-ok={String(result.ok)}>
              {result.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
